import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Rectangle,
} from "recharts";

type MonthRow = {
  monthName: string;                 // "Jan", "Feb", "Mar", ... (or "January")
  totalQuotations: number | string;  // may arrive as string -> coerce
  totalQuotationAmount: number | string; // may arrive as string -> coerce
};

export default function TotalPricePerDay({ data }: { data: MonthRow[] }) {
  const navigate = useNavigate();

  // Ensure numerics for Recharts & tooltip
  const chartData = useMemo(
    () =>
      (data || []).map((d) => ({
        ...d,
        totalQuotations: Number(d.totalQuotations ?? 0),
        totalQuotationAmount: Number(d.totalQuotationAmount ?? 0),
      })),
    [data]
  );

  const fmtInt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
  const fmtMoneyAxis = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    notation: "compact",
    maximumFractionDigits: 2,
  });
  const fmtMoneyFull = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });

  const handleClick = (row: MonthRow) => {
    if (!row?.monthName) return;
    const currentYear = new Date().getFullYear();
    // Works for "Jan" or "January"
    const monthIndex = new Date(`${row.monthName} 1, ${currentYear}`).getMonth();
    if (isNaN(monthIndex)) return;
    // Keep your existing route pattern:
    navigate(`/admin/quotation_list/0/${monthIndex + 1}/${currentYear}`);
  };

  if (!chartData?.length) return null;

  return (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart
        data={chartData}
        margin={{ top: 5, right: 12, left: 0, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="monthName" />
        {/* Left axis: counts */}
        <YAxis
          yAxisId="left"
          allowDecimals={false}
          tickFormatter={(v) => fmtInt.format(Number(v) || 0)}
          width={52}
        />
        {/* Right axis: money */}
        <YAxis
          yAxisId="right"
          orientation="right"
          width={92}
          domain={[0, (max: number) => (max && max > 0 ? max * 1.1 : 1)]}
          tickFormatter={(v) => fmtMoneyAxis.format(Number(v) || 0)}
        />
        <Tooltip
          formatter={(value: any, name: any) => {
            if (name === "totalQuotations") {
              return [fmtInt.format(Number(value || 0)), "Quotations"];
            }
            if (name === "totalQuotationAmount") {
              return [fmtMoneyFull.format(Number(value || 0)), "Total Amount"];
            }
            return [value, name];
          }}
          labelFormatter={(label) => `Month: ${label}`}
        />
        <Legend />

        {/* Bars: total quotations */}
        <Bar
          yAxisId="left"
          dataKey="totalQuotations"
          name="Quotations"
          fill="#B3CDAD"
          radius={[6, 6, 0, 0]}
          activeBar={<Rectangle fill="#FFB3C7" stroke="#7D2E68" />}
          onClick={(e: any) => e?.payload && handleClick(e.payload)}
        />

        {/* Line: total quotation amount */}
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="totalQuotationAmount"
          name="Total Amount"
          strokeWidth={3}
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
          onClick={(e: any) => e?.payload && handleClick(e.payload)}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
